import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../shared/storage/firebase';
import GoogleMapsService from '../../../shared/services/GoogleMapsService';
import {
  coordinateForListing,
  displayCurriculum,
  displaySeason,
  formatListingDateRange,
  formatListingTime,
  isCurrentOrUpcomingListing,
  listingFromSquad,
  listingNeedsStaffing,
  mapClusterKey,
  StaffSquadListing,
  StaffSquadLocation,
} from '../utils/staffSquadListing';
import '../styles/SquadMap.css';

type Filters = {
  curriculum: string;
  season: string;
  state: string;
  staffing: string;
};

const INITIAL_FILTERS: Filters = {
  curriculum: '',
  season: '',
  state: '',
  staffing: '',
};

function buildInfoWindow(
  listings: StaffSquadListing[],
  locations: Record<string, StaffSquadLocation>,
): HTMLElement {
  const root = document.createElement('div');
  root.className = `squad-map-popup${listings.length > 1 ? ' squad-map-popup-group' : ''}`;

  const first = listings[0];
  const firstLocation = locations[first.locationId || ''];
  const heading = document.createElement('h3');
  heading.textContent = firstLocation?.name
    || first.locationName
    || first.schoolName
    || 'Squad location';
  root.appendChild(heading);

  if (listings.length > 1) {
    const hint = document.createElement('p');
    hint.className = 'squad-map-popup-hint';
    hint.textContent = `${listings.length} squads · scroll to see all`;
    root.appendChild(hint);
  }

  const list = document.createElement('div');
  list.className = 'squad-map-popup-list';
  listings.forEach((listing) => {
    const item = document.createElement('section');
    item.className = 'squad-map-popup-item';
    const title = document.createElement('strong');
    title.textContent = listing.title || 'Squad';
    item.appendChild(title);
    [
      `${displayCurriculum(listing)} · ${displaySeason(listing)}`,
      formatListingDateRange(listing),
      formatListingTime(listing),
      listingNeedsStaffing(listing) ? 'Teachers wanted' : 'Staffing assigned',
    ].forEach((text, index) => {
      const line = document.createElement('p');
      line.textContent = text;
      if (index === 3 && listingNeedsStaffing(listing)) line.className = 'squad-map-popup-needed';
      item.appendChild(line);
    });
    list.appendChild(item);
  });
  root.appendChild(list);
  const keepPointerInside = (event: Event) => event.stopPropagation();
  root.addEventListener('wheel', keepPointerInside, { passive: true });
  root.addEventListener('touchmove', keepPointerInside, { passive: true });
  return root;
}

async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  if (!address || !window.google?.maps?.Geocoder) return null;
  const geocoder = new window.google.maps.Geocoder();
  return new Promise((resolve) => {
    geocoder.geocode({ address }, (results: any[], status: string) => {
      const point = results?.[0]?.geometry?.location;
      if (status !== 'OK' || !point) {
        resolve(null);
        return;
      }
      resolve({ lat: point.lat(), lng: point.lng() });
    });
  });
}

const SquadMap: React.FC = () => {
  const mapNodeRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any>(null);
  const [listings, setListings] = useState<StaffSquadListing[]>([]);
  const [locations, setLocations] = useState<Record<string, StaffSquadLocation>>({});
  const [resolvedCoordinates, setResolvedCoordinates] = useState<Record<string, { lat: number; lng: number }>>({});
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    const initialize = async () => {
      setLoading(true);
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        if (!apiKey || apiKey === 'YOUR_API_KEY') {
          throw new Error('Google Maps API key is not configured.');
        }
        if (!GoogleMapsService.isApiLoaded()) await GoogleMapsService.load(apiKey);
        if (canceled || !mapNodeRef.current) return;

        mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
          center: { lat: 41.8781, lng: -87.6298 },
          zoom: 7,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        });

        // Ensure older and newly changed squads are projected before loading.
        // The callable is throttled server-side; public squads remain as a safe
        // fallback if Functions are unavailable.
        try {
          await httpsCallable(getFunctions(), 'ensureStaffSquadListingsProjection')({});
        } catch (projectionError) {
          console.warn('Could not refresh staff squad listings; using available projections.', projectionError);
        }

        const [staffResult, publicResult, locationsResult, squadsResult] = await Promise.allSettled([
          getDocs(collection(db, 'staffSquadListings')),
          getDocs(collection(db, 'publicSquads')),
          getDocs(collection(db, 'locations')),
          getDocs(collection(db, 'squads')),
        ]);
        if (canceled) return;

        const locationRecords = locationsResult.status === 'fulfilled'
          ? Object.fromEntries(locationsResult.value.docs.map((item) => [
            item.id,
            { id: item.id, ...item.data() } as StaffSquadLocation,
          ]))
          : {};
        setLocations(locationRecords);

        const publicListings = publicResult.status === 'fulfilled'
          ? publicResult.value.docs.map((item) => ({ id: item.id, ...item.data() } as StaffSquadListing))
          : [];
        const staffListings = staffResult.status === 'fulfilled'
          ? staffResult.value.docs.map((item) => ({ id: item.id, ...item.data() } as StaffSquadListing))
          : [];
        const liveListings = squadsResult.status === 'fulfilled'
          ? squadsResult.value.docs.map((item) => listingFromSquad(
            { id: item.id, ...item.data() } as StaffSquadListing & { leadTeacherId?: string; termId?: string },
            locationRecords[String((item.data() as { locationId?: string }).locationId || '')],
          ))
          : [];
        const byId = new Map<string, StaffSquadListing>();
        publicListings.forEach((listing) => byId.set(listing.id, listing));
        liveListings.forEach((listing) => byId.set(listing.id, { ...byId.get(listing.id), ...listing }));
        staffListings.forEach((listing) => byId.set(listing.id, { ...byId.get(listing.id), ...listing }));
        setListings(Array.from(byId.values()).filter((listing) => isCurrentOrUpcomingListing(listing)));
      } catch (caught) {
        console.error('Failed to load squad discovery map:', caught);
        if (!canceled) setError(caught instanceof Error ? caught.message : 'Failed to load squad map.');
      } finally {
        if (!canceled) setLoading(false);
      }
    };
    void initialize();
    return () => {
      canceled = true;
      markersRef.current.forEach((marker) => marker.setMap?.(null));
      markersRef.current = [];
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!GoogleMapsService.isApiLoaded() || listings.length === 0) return;
    let canceled = false;
    const resolveMissing = async () => {
      const updates: Record<string, { lat: number; lng: number }> = {};
      const geocodedAddresses = new Map<string, { lat: number; lng: number } | null>();
      for (const listing of listings) {
        if (canceled || coordinateForListing(listing, locations[listing.locationId || ''])) continue;
        if (listing.locationId && resolvedCoordinates[listing.locationId]) {
          updates[listing.id] = resolvedCoordinates[listing.locationId];
          continue;
        }
        const location = locations[listing.locationId || ''];
        const address = String(location?.address || listing.location?.address || '').trim();
        if (!address || resolvedCoordinates[listing.id]) continue;
        let coordinate = geocodedAddresses.get(address);
        if (coordinate === undefined) {
          coordinate = await geocodeAddress(address);
          geocodedAddresses.set(address, coordinate);
        }
        if (coordinate) {
          updates[listing.id] = coordinate;
          if (listing.locationId) updates[listing.locationId] = coordinate;
        }
      }
      if (!canceled && Object.keys(updates).length) {
        setResolvedCoordinates((previous) => ({ ...previous, ...updates }));
      }
    };
    void resolveMissing();
    return () => { canceled = true; };
  }, [listings, locations, resolvedCoordinates]);

  const filterOptions = useMemo(() => ({
    curricula: Array.from(new Set(listings.map(displayCurriculum))).sort(),
    seasons: Array.from(new Set(listings.map(displaySeason))).sort(),
    states: Array.from(new Set(listings.map((listing) => {
      const location = locations[listing.locationId || ''];
      return String(location?.state || listing.location?.state || '').trim();
    }).filter(Boolean))).sort(),
  }), [listings, locations]);

  const filteredListings = useMemo(() => listings.filter((listing) => {
    const location = locations[listing.locationId || ''];
    if (filters.curriculum && displayCurriculum(listing) !== filters.curriculum) return false;
    if (filters.season && displaySeason(listing) !== filters.season) return false;
    if (filters.state && String(location?.state || listing.location?.state || '') !== filters.state) return false;
    if (filters.staffing === 'needed' && !listingNeedsStaffing(listing)) return false;
    if (filters.staffing === 'assigned' && listingNeedsStaffing(listing)) return false;
    return true;
  }), [listings, locations, filters]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !GoogleMapsService.isApiLoaded()) return;
    markersRef.current.forEach((marker) => marker.setMap?.(null));
    markersRef.current = [];
    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow({ maxWidth: 340 });
    }
    const infoWindow = infoWindowRef.current;
    infoWindow.close();

    const bounds = new window.google.maps.LatLngBounds();
    const groups = new Map<string, {
      position: { lat: number; lng: number };
      listings: StaffSquadListing[];
    }>();
    filteredListings.forEach((listing) => {
      const location = locations[listing.locationId || ''];
      const position = coordinateForListing(listing, location)
        || (listing.locationId ? resolvedCoordinates[listing.locationId] : undefined)
        || resolvedCoordinates[listing.id];
      if (!position) return;
      const key = mapClusterKey(listing, position);
      const group = groups.get(key);
      if (group) group.listings.push(listing);
      else groups.set(key, { position, listings: [listing] });
    });

    groups.forEach(({ position, listings: groupedListings }) => {
      const first = groupedListings[0];
      const isGroup = groupedListings.length > 1;
      const labelText = isGroup
        ? `${groupedListings.length} squads here`
        : `${displayCurriculum(first)} · ${displaySeason(first)}`;
      const anyNeedsStaffing = groupedListings.some(listingNeedsStaffing);
      const marker = new window.google.maps.Marker({
        map,
        position,
        title: isGroup ? `${groupedListings.length} squads at this location` : `${first.title} — ${labelText}`,
        label: {
          text: labelText.length > 28 ? `${labelText.slice(0, 27)}…` : labelText,
          color: '#25332a',
          fontSize: '11px',
          fontWeight: '700',
          className: 'squad-map-marker-label',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: anyNeedsStaffing ? '#f59e0b' : '#0288d1',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 3,
          scale: 10,
          labelOrigin: new window.google.maps.Point(0, -2.1),
        },
      });
      const openCluster = () => {
        infoWindow.setContent(buildInfoWindow(groupedListings, locations));
        infoWindow.open({ map, anchor: marker });
      };
      marker.addListener('mouseover', openCluster);
      marker.addListener('click', openCluster);
      markersRef.current.push(marker);
      bounds.extend(position);
    });

    const mapClick = map.addListener('click', () => infoWindow.close());

    if (markersRef.current.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(12);
    } else if (markersRef.current.length > 1) {
      map.fitBounds(bounds, 54);
    }

    return () => {
      mapClick.remove();
      infoWindow.close();
      markersRef.current.forEach((marker) => marker.setMap?.(null));
      markersRef.current = [];
    };
  }, [filteredListings, locations, resolvedCoordinates]);

  const locatedIds = new Set(filteredListings.filter((listing) => (
    coordinateForListing(listing, locations[listing.locationId || ''])
    || (listing.locationId && resolvedCoordinates[listing.locationId])
    || resolvedCoordinates[listing.id]
  )).map((listing) => listing.id));
  const unlocated = filteredListings.filter((listing) => !locatedIds.has(listing.id));

  const updateFilter = (name: keyof Filters, value: string) => {
    setFilters((previous) => ({ ...previous, [name]: value }));
  };

  return (
    <section className="squad-map-container" aria-label="Discover nearby squads">
      <header className="map-header">
        <div>
          <h2>Squads near you</h2>
          <p>Explore upcoming programs and opportunities to teach nearby.</p>
        </div>
        <div className="map-legend" aria-label="Map legend">
          <span><i className="legend-dot assigned" /> Staffed</span>
          <span><i className="legend-dot needed" /> Teachers wanted</span>
        </div>
      </header>

      <div className="map-filters">
        <select value={filters.curriculum} onChange={(event) => updateFilter('curriculum', event.target.value)} aria-label="Filter by curriculum">
          <option value="">All curricula</option>
          {filterOptions.curricula.map((value) => <option key={value}>{value}</option>)}
        </select>
        <select value={filters.season} onChange={(event) => updateFilter('season', event.target.value)} aria-label="Filter by season">
          <option value="">All seasons</option>
          {filterOptions.seasons.map((value) => <option key={value}>{value}</option>)}
        </select>
        <select value={filters.state} onChange={(event) => updateFilter('state', event.target.value)} aria-label="Filter by state">
          <option value="">All states</option>
          {filterOptions.states.map((value) => <option key={value}>{value}</option>)}
        </select>
        <select value={filters.staffing} onChange={(event) => updateFilter('staffing', event.target.value)} aria-label="Filter by staffing">
          <option value="">All staffing</option>
          <option value="needed">Teachers wanted</option>
          <option value="assigned">Staffed</option>
        </select>
        {Object.values(filters).some(Boolean) && (
          <button type="button" onClick={() => setFilters(INITIAL_FILTERS)}>Clear filters</button>
        )}
      </div>

      <div className="map-content-wrapper">
        {loading && <div className="map-loading"><span className="map-loading-spinner">⌖</span><p>Loading squad map…</p></div>}
        {error && <div className="map-error"><h3>Map unavailable</h3><p>{error}</p></div>}
        <div ref={mapNodeRef} className="map-container" aria-hidden={loading || Boolean(error)} />
      </div>

      {!loading && !error && (
        <footer className="map-summary">
          <strong>{locatedIds.size}</strong> squads shown
          {unlocated.length > 0 && (
            <details>
              <summary>{unlocated.length} without map coordinates</summary>
              <ul>{unlocated.map((listing) => <li key={listing.id}>{listing.title}</li>)}</ul>
            </details>
          )}
        </footer>
      )}
    </section>
  );
};

export default SquadMap;
